import { motion } from "framer-motion";
import { Cpu, Gauge, ShieldCheck, ArrowUpRight } from "lucide-react";

const TOOLS = [
  {
    n: "01",
    tag: "In-house · Proprietary",
    icon: Gauge,
    title: "Credit Recovery Suite",
    desc: "Applicativo proprietario per la gestione end-to-end del recupero del credito commerciale: monitoraggio scadenze, solleciti automatici, dossier legale e tempi di incasso ridotti.",
    kpi: [
      ["-40%", "tempo medio di incasso"],
      ["+65%", "tasso di recupero"],
    ],
  },
  {
    n: "02",
    tag: "In-house · Proprietary",
    icon: ShieldCheck,
    title: "Contract Risk Monitor",
    desc: "Piattaforma per mappare i contratti in essere, individuare clausole critiche, scadenze e livelli di esposizione. Alert automatici sui rischi legali e regolatori.",
    kpi: [
      ["360°", "visione contrattuale"],
      ["Real-time", "alert su rischi & scadenze"],
    ],
  },
  {
    n: "03",
    tag: "In-house · Proprietary",
    icon: Cpu,
    title: "Compliance & Governance Cockpit",
    desc: "Cruscotto integrato per la governance societaria: adempimenti, deleghe, delibere, adempimenti UBO, tracciamento decisioni e trail documentale.",
    kpi: [
      ["Audit-ready", "documentazione strutturata"],
      ["-50%", "tempo di reporting"],
    ],
  },
];

export default function LegalTech() {
  return (
    <section
      id="legaltech"
      className="relative py-24 lg:py-32 border-t border-[#FAF9F6]/10 overflow-hidden"
      data-testid="legaltech-section"
    >
      <div className="absolute inset-0 grid-lines opacity-[0.18] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="overline">Legal Tech Proprietario</span>
              <span className="h-px flex-1 bg-[#C5A880]/40 max-w-[80px]" />
              <span className="font-mono text-[10px] tracking-widest text-[#C5A880] uppercase">
                v.2026
              </span>
            </div>
            <h2 className="font-display text-4xl lg:text-6xl font-medium leading-[1.02] tracking-tight">
              Non solo consulenza.
              <span className="block text-[#FAF9F6]/50 italic font-normal">
                Anche tecnologia proprietaria.
              </span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 lg:pl-8 lg:border-l border-[#FAF9F6]/10"
          >
            <p className="text-[15px] lg:text-base text-[#FAF9F6]/70 leading-relaxed">
              Lo Studio ha sviluppato applicativi software proprietari a
              supporto delle aziende nella gestione operativa del rischio
              legale — dal <span className="text-[#C5A880]">recupero del
              credito commerciale</span> alla governance contrattuale — per
              ridurre tempi, contenziosi ed esposizioni.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0A0A0A]/15 border border-[#0A0A0A]/15">
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.article
                key={t.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-[#EAECE7] text-[#0A0A0A] p-8 lg:p-10 group hover:bg-[#F2F3EE] transition-colors relative"
                data-testid={`legaltech-card-${t.n}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="h-11 w-11 border border-[#C5A880] flex items-center justify-center bg-[#0A0A0A] group-hover:bg-[#161616] transition-colors">
                    <Icon size={18} className="text-[#C5A880]" />
                  </div>
                  <span className="font-mono text-[10px] tracking-widest text-[#0A0A0A]/45">
                    {t.n} / 03
                  </span>
                </div>

                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#1B3B36] mb-3">
                  {t.tag}
                </div>
                <h3 className="font-display text-2xl lg:text-[26px] font-medium leading-tight tracking-tight mb-4">
                  {t.title}
                </h3>
                <p className="text-sm text-[#0A0A0A]/70 leading-relaxed">
                  {t.desc}
                </p>

                <div className="mt-8 pt-6 border-t border-[#0A0A0A]/12 grid grid-cols-2 gap-6">
                  {t.kpi.map(([k, v]) => (
                    <div key={k}>
                      <div className="font-display text-2xl text-[#1B3B36] font-medium">
                        {k}
                      </div>
                      <div className="text-[11px] text-[#0A0A0A]/60 mt-1 leading-snug">
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8 border-t border-[#FAF9F6]/10"
        >
          <p className="text-xs text-[#FAF9F6]/55 max-w-xl leading-relaxed">
            Software sviluppati e mantenuti direttamente dallo Studio.
            Interoperabili con i sistemi gestionali aziendali. Deploy on-premise
            o in modalità SaaS dedicata.
          </p>
          <button
            onClick={() =>
              document
                .getElementById("engage")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-ghost inline-flex items-center gap-3 px-6 py-3 text-[11px] font-semibold tracking-[0.22em] uppercase"
            data-testid="legaltech-cta"
          >
            Richiedi una demo <ArrowUpRight size={14} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
